import { useState } from 'react'
import { show as showModal } from '@ebay/nice-modal-react'
import { Icon } from '@iconify/react'
import { pluralize } from '@pepper/utils/src/function/pluralize'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Checkbox,
  Modal,
  Tooltip,
  useGlobalState,
  useShowMessage,
} from '@pepper/ui'

import { useGenerateBulkSeoGuidelines } from 'utils/hooks/useGenerateBulkSeoGuidelines'
import { useAddTopics } from 'utils/hooks/useAddTopics'
import SelectedTopicsHeader from '../SelectedTopicsHeader'

import { SuggestedTopic } from 'types/topic'

import s from './AddTopicsToProjectModal.module.css'

type AddTopicsToProjectProps = {
  open: boolean
  selectedTopics: SuggestedTopic[]
  companyId: string
  projectId: string
  domainRegion: string
  onClose: () => void
  onSuccess: () => void
}

export const AddTopicsToProjectModal: React.FC<AddTopicsToProjectProps> = ({
  open,
  selectedTopics,
  companyId,
  projectId,
  domainRegion,
  onClose,
  onSuccess,
}) => {
  const showMessage = useShowMessage()
  const history = useHistory()
  const { subscription, trialExhaust } = useGlobalState()
  const [shouldAttachSeoGuidelines, setShouldAttachSeoGuidelines] =
    useState(false)
  const [isAttaching, setIsAttaching] = useState(false)

  const { mutateAsync: addTopics, isLoading: isAdding } = useAddTopics()
  const { mutateAsync: generateBulkSeoGuidelines } =
    useGenerateBulkSeoGuidelines()

  const handleClose = () => {
    setShouldAttachSeoGuidelines(false)
    onClose()
  }

  const handleAddTopic = async (attachSeoGuideline: boolean) => {
    const isTrialExpired = subscription?.trialStatus === 'expired'
    const isLimitExhausted = trialExhaust?.seoContentResearch

    if (attachSeoGuideline && (isTrialExpired || isLimitExhausted)) {
      showModal('buy-cmp', {
        ...(isLimitExhausted && {
          id: 'seoContentResearch',
        }),
      })
      return
    }

    const topicIds = selectedTopics.map(topic => topic.id)
    setIsAttaching(attachSeoGuideline)
    const { status, data, error } = await addTopics({
      companyId,
      projectId,
      topicIds,
    })
    if (status === 'success') {
      if (attachSeoGuideline && data) {
        const seoGuidelineResponse = await generateBulkSeoGuidelines({
          companyId,
          topics: data.map(topic => ({
            topic_id: topic.id,
            topic: topic.name,
            region_code: domainRegion,
          })),
        })
        if (seoGuidelineResponse.status === 'error') {
          showMessage({
            type: 'error',
            message: seoGuidelineResponse.error,
          })
        }
      }
      onSuccess()
      const isContentIdeasPage = history.location.pathname === '/content-ideas'
      if (isContentIdeasPage) {
        showMessage({
          type: 'success',
          message: (
            <div className={s.message}>
              {pluralize(selectedTopics.length, 'topic')} added to the project
              <Button
                type="default"
                onClick={() => history.push(`/project/${projectId}/topics`)}
              >
                Go to project
              </Button>
            </div>
          ),
        })
      } else if (history.location.pathname.includes('keywords')) {
        showMessage({
          type: 'success',
          message: (
            <div className={s.message}>
              {pluralize(selectedTopics.length, 'topic')} added to the project
              <Button
                type="default"
                onClick={() => history.push(`/project/${projectId}/topics`)}
              >
                Go to topics
              </Button>
            </div>
          ),
        })
      } else {
        showMessage({
          type: 'success',
          message: `${pluralize(
            selectedTopics.length,
            'topic'
          )} added to the project`,
        })
      }
      handleClose()
    } else {
      showMessage({ type: 'error', message: error })
    }
    setIsAttaching(false)
  }

  return (
    <Modal
      title="Add Topics to the project"
      visible={open}
      onCancel={handleClose}
      footer={null}
    >
      <div className={s.wrapper}>
        <SelectedTopicsHeader
          selectedTopics={selectedTopics
            .filter(topic => !!topic)
            .map(topic => ({
              id: topic.id,
              name: topic.name,
            }))}
        />
      </div>
      <div className={s.seoGuidelines}>
        <Checkbox
          checked={shouldAttachSeoGuidelines}
          onChange={() => setShouldAttachSeoGuidelines(prevVal => !prevVal)}
        />
        Create SEO Content Guidelines for each topic
        <Tooltip
          placement="bottom"
          title="Guidelines include best practices in terms of SEO, readability and
          more from top ranking pages."
        >
          <Icon icon="lucide:info" />
        </Tooltip>
      </div>

      <div className={s.footer}>
        <Button type="link" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="button"
          onClick={() => handleAddTopic(shouldAttachSeoGuidelines)}
          loading={
            shouldAttachSeoGuidelines ? isAttaching : !isAttaching && isAdding
          }
        >
          {shouldAttachSeoGuidelines
            ? `Add ${pluralize(
                selectedTopics.length,
                'Topic'
              )}  with SEO Guidelines`
            : `Add ${pluralize(selectedTopics.length, 'Topic')} `}
        </Button>
      </div>
    </Modal>
  )
}
